import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Wallet,
  TrendingUp,
  DollarSign,
  PieChart,
  FileText,
  Download,
  Building2,
  Calendar,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LPDashboardPage() {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, portfolioRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/lp/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/lp/portfolio`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/lp/documents`, { headers: getAuthHeaders() })
      ]);
      
      setDashboardData(dashboardRes.data);
      setPortfolioData(portfolioRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Failed to fetch LP dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount?.toLocaleString() || 0}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      quarterly_report: 'Quarterly Report',
      capital_call: 'Capital Call',
      distribution_notice: 'Distribution Notice',
      other: 'Document'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="lp-dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
          {isAdmin ? 'LP Overview' : 'Investor Dashboard'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isAdmin ? 'View all LP commitments and fund performance' : 'Your investment summary and portfolio performance'}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card data-testid="total-commitment-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-widest">Total Commitment</p>
                <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                  {formatCurrency(dashboardData?.total_commitment || 0)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Wallet className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="paid-in-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-widest">Paid-In Capital</p>
                <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                  {formatCurrency(dashboardData?.total_paid_in || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {dashboardData?.total_commitment > 0 
                    ? `${((dashboardData?.total_paid_in / dashboardData?.total_commitment) * 100).toFixed(0)}% called`
                    : '0% called'}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <ArrowUpRight className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="distributions-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-widest">Distributions</p>
                <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                  {formatCurrency(dashboardData?.total_distributions || 0)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <ArrowDownRight className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="moic-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-widest">MOIC</p>
                <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                  {dashboardData?.moic?.toFixed(2) || '0.00'}x
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  NAV: {formatCurrency(dashboardData?.net_asset_value || 0)}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          {isAdmin && <TabsTrigger value="commitments">All Commitments</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Your Commitments */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Your Commitments</CardTitle>
                <CardDescription>Fund commitment summary</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.commitments?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboardData.commitments.map((commitment) => (
                      <div key={commitment.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-900">{commitment.fund_name}</h4>
                          <Badge variant="secondary">
                            {commitment.ownership_percentage?.toFixed(2) || 0}% ownership
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Committed</p>
                            <p className="font-medium">{formatCurrency(commitment.commitment_amount)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Paid In</p>
                            <p className="font-medium">{formatCurrency(commitment.paid_in_capital)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Distributed</p>
                            <p className="font-medium">{formatCurrency(commitment.distributions)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No commitments found</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Portfolio Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Portfolio Summary</CardTitle>
                <CardDescription>Investment allocation overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Total Invested</p>
                      <p className="text-xl font-heading font-bold">
                        {formatCurrency(portfolioData?.summary?.total_invested || 0)}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Current Value</p>
                      <p className="text-xl font-heading font-bold">
                        {formatCurrency(portfolioData?.summary?.total_current_value || 0)}
                      </p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-3">Sector Allocation</p>
                    <div className="space-y-2">
                      {Object.entries(portfolioData?.summary?.sector_breakdown || {}).map(([sector, amount]) => (
                        <div key={sector} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{sector}</span>
                          <span className="text-sm font-medium">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Portfolio Companies</CardTitle>
              <CardDescription>Detailed view of fund investments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {portfolioData?.investments?.map((investment) => (
                  <div key={investment.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-heading font-semibold text-lg text-slate-900">
                          {investment.company_name}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary">{investment.sector}</Badge>
                          <Badge variant="outline">{investment.stage}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Current Valuation</p>
                        <p className="text-lg font-heading font-bold text-slate-900">
                          {formatCurrency(investment.current_valuation)}
                        </p>
                        <p className="text-sm text-emerald-600">
                          {investment.moic?.toFixed(2) || '1.00'}x MOIC
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm bg-slate-50 rounded-lg p-3">
                      <div>
                        <p className="text-slate-500">Invested</p>
                        <p className="font-medium">{formatCurrency(investment.investment_amount)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Ownership</p>
                        <p className="font-medium">{investment.ownership_percentage?.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Investment Date</p>
                        <p className="font-medium">{formatDate(investment.investment_date)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Status</p>
                        <Badge className={
                          investment.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          investment.status === 'exited' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {investment.status}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Funding Rounds */}
                    {investment.funding_rounds?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">Funding History</p>
                        <div className="flex flex-wrap gap-2">
                          {investment.funding_rounds.map((round) => (
                            <div key={round.id} className="text-xs bg-white border rounded px-2 py-1">
                              <span className="font-medium">{round.round_name}</span>
                              <span className="text-slate-500 ml-1">
                                {formatCurrency(round.amount_invested)} • {formatDate(round.round_date)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {(!portfolioData?.investments || portfolioData.investments.length === 0) && (
                  <div className="text-center py-12 text-slate-500">
                    <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No portfolio companies yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Fund Documents</CardTitle>
              <CardDescription>Quarterly reports and investor communications</CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded">
                              <FileText className="h-4 w-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{doc.title}</p>
                              {doc.description && (
                                <p className="text-sm text-slate-500">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {getDocumentTypeLabel(doc.document_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(doc.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <a href={doc.storage_path} target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No documents available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Commitments Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="commitments">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">All LP Commitments</CardTitle>
                <CardDescription>Overview of all limited partner investments</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.commitments?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>LP Name</TableHead>
                        <TableHead>Fund</TableHead>
                        <TableHead>Commitment</TableHead>
                        <TableHead>Paid In</TableHead>
                        <TableHead>Distributions</TableHead>
                        <TableHead>Ownership</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.commitments.map((commitment) => (
                        <TableRow key={commitment.id}>
                          <TableCell className="font-medium">{commitment.lp_name}</TableCell>
                          <TableCell>{commitment.fund_name}</TableCell>
                          <TableCell>{formatCurrency(commitment.commitment_amount)}</TableCell>
                          <TableCell>{formatCurrency(commitment.paid_in_capital)}</TableCell>
                          <TableCell>{formatCurrency(commitment.distributions)}</TableCell>
                          <TableCell>{commitment.ownership_percentage?.toFixed(2) || 0}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No LP commitments recorded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
